if (!customElements.get('country-selector-form')) {
  class CountrySelectorForm extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        countryList: this.querySelector('.country-selector__list'),
        countryLinks: this.querySelectorAll('a.country-selector__item'),
        formInput: this.querySelector('input[name="country_code"]'),
        listTrigger: this.querySelector('.country-selector__trigger'),
        searchFilter: this.querySelector('input[name="country_search"]'),
        liveRegion: this.querySelector('#sr-country-search-results'),
      };

      // Define event listeners
      this.listeners = {
        countryLinks: {
          click: this.onItemClick.bind(this)
        },
        listTrigger: {
          click: this.openSelector.bind(this),
          focusout: this.handleFocusOut.bind(this),
          keyup: this.onContainerKeyUp.bind(this)
        },
        searchFilter: {
          keyup: this.filterCountries.bind(this),
          keydown: this.onSearchKeyDown.bind(this)
        }
      };

      // Add event listeners
      Object.entries(this.listeners).forEach(([elementKey, events]) => {
        const element = this.elements[elementKey];
        if (!element) return;

        const elements = element.forEach ? element : [element];

        elements.forEach(el => {
          Object.entries(events).forEach(([event, handler]) => {
            el.addEventListener(event, handler);
          });
        });
      });
    }

    disconnectedCallback() {
      Object.entries(this.listeners).forEach(([elementKey, events]) => {
        const element = this.elements[elementKey];
        if (!element) return;

        const elements = element.forEach ? element : [element];

        elements.forEach(el => {
          Object.entries(events).forEach(([event, handler]) => {
            el.removeEventListener(event, handler);
          });
        });
      });
    }

    normalizeString(str) {
      return str
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase();
    }

    filterCountries() {
      const searchValue = this.normalizeString(this.elements.searchFilter.value);
      const allCountries = this.elements.countryLinks;
      let visibleCountries = allCountries.length;

      allCountries.forEach((item) => {
        const countryName = this.normalizeString(item.querySelector('.country-selector__item__country').textContent);
        const countryCurrency = this.normalizeString(item.querySelector('.country-selector__item__currency').textContent);
        if (countryName.indexOf(searchValue) > -1 || countryCurrency.indexOf(searchValue) > -1) {
          item.parentElement.classList.remove('hidden');
          visibleCountries++;
        } else {
          item.parentElement.classList.add('hidden');
          visibleCountries--;
        }
      });

      if (this.elements.liveRegion) {
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
          '[count]',
          visibleCountries
        );
      }

      this.querySelector('.country-selector').scrollTop = 0;
      this.querySelector('.country-selector__list').scrollTop = 0;
    }

    onSearchKeyDown(event) {
      if (event.code.toUpperCase() === 'ENTER') {
        event.preventDefault();
      }
    }

    onContainerKeyUp(event) {
      if (event.code.toUpperCase() !== 'ESCAPE') return;

      this.closeSelector();
      this.elements.listTrigger?.focus();
    }

    onItemClick(event) {
      event.preventDefault();
      // If the current country value is selected, do not resubmit the form, just close the selector
      if (this.elements.formInput.value === event.currentTarget.dataset.value) {
        this.dispatchEvent(new CustomEvent('formExit', { bubbles: true }));
        this.closeSelector();
        return;
      };
      const form = this.querySelector('form');
      this.elements.formInput.value = event.currentTarget.dataset.value;
      if (form) form.submit();
    }

    openSelector() {
      if (!this.elements.listTrigger) return;
      this.elements.listTrigger.focus();
      this.elements.countryList.toggleAttribute('hidden');
      this.elements.listTrigger.setAttribute('aria-expanded', (this.elements.listTrigger.getAttribute('aria-expanded') === 'false').toString());
      this.dispatchEvent(new CustomEvent('content-opened', { bubbles: true }));
    }

    closeSelector() {
      if (!this.elements.listTrigger) return;
      this.elements.listTrigger.setAttribute('aria-expanded', 'false');
      this.dispatchEvent(new CustomEvent('content-closed', { bubbles: true }));
      this.elements.countryList?.setAttribute('hidden', '');
    }

    handleFocusOut(event) {
      const shouldClose = event.relatedTarget && event.relatedTarget.nodeName === 'BUTTON';
      if (event.relatedTarget === null || shouldClose) {
        this.closeSelector();
      }
    }

    reset() {
      if (this.elements.searchFilter) {
        this.elements.searchFilter.value = '';
      }

      // Show all countries by removing hidden class
      this.elements.countryLinks.forEach((item) => {
        item.parentElement.classList.remove('hidden');
      });

      // Reset live region
      if (this.elements.liveRegion) {
        this.elements.liveRegion.innerHTML = '';
      }
    }
  }

  customElements.define('country-selector-form', CountrySelectorForm);
}